'use client';
/**
 * R14b 전역 지역 칩 「전국 ▾」 — 독립 컴포넌트(통합 담당이 R14a 의 NavRegionSlot · kd-pagehead 에 꽂는다).
 *
 *   <GlobalRegionChip data={headerRegion} variant="masthead" />   데스크톱 마스트 우측 · 모바일 서브마스트 좌측
 *   <GlobalRegionChip data={headerRegion} variant="pagehead" />   kd-pagehead 2줄 우측
 *   <GlobalRegionChip data={headerRegion} variant="hero" />       홈 히어로 검색창 앞
 *   <GlobalRegionChip data={headerRegion} mode="select" onSelect={fn} />  검색 시트 안(이동 없이 우선 지역만 고른다)
 *
 * data = HeaderRegionData — (main)/layout 이 이미 만들어 ClientShell → Navigation 으로 내리는 그 값(regions 표 · 허브 href).
 *
 * 규칙(NAV14 §R14b):
 *   · 서버 HTML·첫 수화 렌더는 언제나 「전국」. 현재 지역(경로·?r=)·기억(localStorage)은 useEffect 뒤에만.
 *   · 기억은 칩 라벨 + `a[data-kd-region-link]` 만 바꾼다. 본문 링크 불변 · 칩 폭 고정(시프트 0) · 자동 이동 없음.
 *   · 적용 = URL. 부동산 → 색인 허브, 게시판·블로그·홈 → ?r= (region-scope.ts buildApplyHref).
 *   · 선택지는 <button> — ?r= URL 을 <a href> 로 내보내지 않는다(크롤러가 파라미터 변형을 줍지 않게).
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import type { HeaderRegionData } from '@/lib/v3/header-region-match';
import {
  applyOutcome, buildApplyHref, currentScope, findScopeNode, orderTops, readRegionMemory, regionLinkHref, scopeContext, scopeLabel,
  searchScope, writeRegionMemory, type ScopeNode,
} from '@/lib/nav/region-scope';
import { readGeoCookie } from '@/lib/nav/geo-cookie';
import { geoSuggestRecord, geoSuggestText, isRegionHubPath, pickGeoSuggestion, readGeoSuggestRaw, writeGeoSuggestRaw } from '@/lib/nav/geo-suggest';
import { emitRegionParam } from '@/lib/nav/use-region-param';
import { useFocusTrap } from '@/lib/nav/focus-trap';
import { useRegionHint } from './RegionHint';
import { navTrack } from '@/lib/nav/nav-track';
import { trackRx } from '@/lib/rx/track';
import KdRegionSheetView from '@/components/region/KdRegionSheetView';
import './global-nav.css';

export const REGION_MEMORY_EVENT = 'kd:region-memory';

export interface GlobalRegionChipProps {
  data: HeaderRegionData | null | undefined;
  variant?: 'masthead' | 'pagehead' | 'hero';
  /** navigate(기본) = URL 로 적용 · select = 이동 없이 onSelect 만(검색 시트). */
  mode?: 'navigate' | 'select';
  onSelect?: (node: ScopeNode | null) => void;
  className?: string;
  /** nav_region1 v2 — 「주」 칩(마스트 1개)만: 첫 방문 지역 제안(kd_geo) 노출 + 지역 허브 진입 시 기억 동기화. 기본 = masthead·navigate. */
  primary?: boolean;
}


/** 탭·드롭다운 지역 링크 갈아끼우기(원래 href 는 data-kd-region-orig 에 보관 → 전국이면 복원). */
function rewriteRegionLinks(node: ScopeNode | null): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll<HTMLAnchorElement>('a[data-kd-region-link]').forEach((a) => {
    if (a.dataset.kdRegionOrig === undefined) a.dataset.kdRegionOrig = a.getAttribute('href') ?? '';
    const next = node ? regionLinkHref(a.dataset.kdRegionLink, node) : null;
    a.setAttribute('href', next ?? a.dataset.kdRegionOrig);
  });
}

export default function GlobalRegionChip({ data, variant = 'masthead', mode = 'navigate', onSelect, className, primary }: GlobalRegionChipProps) {
  const pathname = usePathname();
  const isPrimary = (primary ?? variant === 'masthead') && mode === 'navigate';
  const router = useRouter();
  // ⚠️ 첫 렌더 = 서버와 같은 「전국」. 여기서 location/localStorage 를 읽으면 수화 불일치 + 서버/봇 HTML 차이.
  // RD-3 C7 — 단, 경로가 지역을 주는 허브(/apt/region/부산 등)는 서버·클라이언트가 같은 pathname 을 보므로 첫 렌더부터 그 지역으로 그린다(수화 일치).
  const [node, setNode] = useState<ScopeNode | null>(() => {
    if (!data || mode === 'select') return null;
    const cur = currentScope(data, pathname, '', null);
    return cur.source === 'path' ? cur.node : null;
  });
  const hintName = useRegionHint();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // 수화 뒤: 현재 지역(경로·?r=) > 기억 > 전국. 경로가 바뀌거나 다른 칩이 기억을 바꾸면 다시.
  useEffect(() => {
    if (!data) return;
    const sync = () => {
      const mem = readRegionMemory();
      let cur = currentScope(data, pathname, window.location.search, mem);
      // nav_region1 v2 (9) — 지역 허브(/apt/region/{시도} · /apt/area/…)에 들어오면 기억도 그 지역으로 → 홈 필터·/calendar 지역 칩이 같은 지역.
      //   주 칩 1개만 쓴다(중복 기록 방지). 현장 상세(페이지 힌트)는 기억을 바꾸지 않는다. 값이 같으면 아무것도 안 한다(이벤트 왕복 1회로 끝).
      if (isPrimary && cur.source === 'path' && cur.node && isRegionHubPath(pathname) && mem !== cur.node.code) {
        writeRegionMemory(cur.node.code, cur.node);
        try { window.dispatchEvent(new Event(REGION_MEMORY_EVENT)); } catch { /* 무시 */ }
      }
      // RD-3 C7 — 경로가 지역을 주지 않는 상세(/apt/{slug}): 페이지가 알린 지역 이름이 기억보다 앞선다.
      if (mode !== 'select' && cur.source !== 'path' && scopeContext(pathname) === 'apt' && hintName) {
        const byHint = findScopeNode(data, data.tops.find((t) => t.label === hintName || t.children.some((c) => c.label === hintName))?.code ?? null);
        if (byHint) cur = { node: byHint, source: 'path' };
      }
      setNode(cur.node);
      rewriteRegionLinks(cur.node);
    };
    sync();
    window.addEventListener(REGION_MEMORY_EVENT, sync);
    window.addEventListener('popstate', sync);
    return () => { window.removeEventListener(REGION_MEMORY_EVENT, sync); window.removeEventListener('popstate', sync); };
  }, [data, pathname, hintName, mode, isPrimary]);

  // nav_region1 v2 (6) — 첫 방문 지역 제안. 수화 뒤에만(서버 HTML·첫 렌더엔 없음 · 크롤러는 kd_geo 자체를 못 받는다).
  const [geo, setGeo] = useState<ScopeNode | null>(null);
  useEffect(() => {
    setGeo(null);
    if (!isPrimary || !data) return;
    // 현장 상세처럼 페이지가 지역을 알리는 화면에서는 제안하지 않는다(그 현장 지역과 다투지 않게).
    if (hintName) return;
    const now = Date.now();
    const hiddenRaw = readGeoSuggestRaw();
    const mem = readRegionMemory();
    const pick = pickGeoSuggestion({
      data,
      geoIso: readGeoCookie(document.cookie),
      memoryCode: mem,
      currentSource: currentScope(data, pathname, window.location.search, mem).source,
      hiddenRaw,
      now,
    });
    if (!pick) return;
    writeGeoSuggestRaw(geoSuggestRecord('seen', now, hiddenRaw)); // 1회 노출 — 다음 쪽·같은 날 재방문엔 다시 안 뜬다
    setGeo(pick);
    trackRx('region_suggest', pick.code, 'shown', { target: window.location.pathname });
  }, [data, pathname, hintName, isPrimary]);
  // 다른 칩·띠가 지역을 고르면(기억 생김) 제안을 거둔다.
  useEffect(() => {
    if (!geo) return;
    const off = () => { if (readRegionMemory()) setGeo(null); };
    window.addEventListener(REGION_MEMORY_EVENT, off);
    return () => window.removeEventListener(REGION_MEMORY_EVENT, off);
  }, [geo]);

  const tops = useMemo(() => orderTops(data?.tops ?? []), [data]);
  const hits = useMemo(() => searchScope(data, q), [data, q]);

  const close = useCallback(() => { setOpen(false); setQ(''); btnRef.current?.focus(); }, []);
  useFocusTrap(open, panelRef, close);

  // 모바일(≤767) 시트는 body 로 포털 — 마스트(sticky z 100)·히어로의 쌓임 맥락에 갇히면 하단 탭바가 시트를 덮는다.
  const [portal, setPortal] = useState(false);
  const toggle = () => {
    if (geo) setGeo(null); // 칩을 직접 열면 제안은 접는다(숨김 기록은 'seen' 그대로)
    if (!open) {
      try { setPortal(mode !== 'select' && window.matchMedia('(max-width: 767px)').matches); } catch { setPortal(false); }
      const topCode = node?.parent?.code ?? node?.code ?? null;
      const t = topCode ? tops.find((x) => x.code === topCode) : null;
      setExpanded(t && t.children.length ? t.code : null);
      navTrack('region_chip_open', { variant, from: node?.code ?? 'national' });
    }
    setOpen((v) => !v);
  };

  const apply = (next: ScopeNode | null, via?: string) => {
    navTrack('region_chip_apply', { variant, code: next?.code ?? 'national', ctx: mode === 'select' ? 'search' : scopeContext(pathname) });
    const href = mode === 'select' ? null : buildApplyHref(pathname, next);
    // nav_region1 — 결과(navigated|filtered|noop)·목적지를 함께(category = `{variant}:{result}` · target → referrer_source 열).
    const out = applyOutcome(pathname, href, typeof window !== 'undefined' ? window.location.pathname + window.location.search : pathname, mode);
    trackRx('region_select_apply', next?.code ?? 'national', `${via ?? variant}:${out.result}`, { target: out.target });
    writeRegionMemory(next?.code ?? null, next);
    setNode(next);
    rewriteRegionLinks(next);
    try { window.dispatchEvent(new Event(REGION_MEMORY_EVENT)); } catch { /* 무시 */ }
    setOpen(false);
    setQ('');
    if (mode === 'select') { onSelect?.(next); btnRef.current?.focus(); return; }
    if (!href) { btnRef.current?.focus(); return; }
    router.push(href);
    // 같은 경로에서 ?r= 만 바뀌면 섬이 다시 마운트되지 않는다 → 알림.
    if (scopeContext(pathname) === 'filter') emitRegionParam(next?.code ?? null);
  };

  const acceptGeo = () => {
    if (!geo) return;
    const g = geo;
    trackRx('region_suggest', g.code, 'accepted', { target: buildApplyHref(pathname, g) ?? window.location.pathname });
    setGeo(null);
    apply(g, 'suggest');
  };
  const dismissGeo = () => {
    if (!geo) return;
    trackRx('region_suggest', geo.code, 'dismissed', { target: window.location.pathname });
    writeGeoSuggestRaw(geoSuggestRecord('dismissed', Date.now(), readGeoSuggestRaw())); // 7일 숨김
    setGeo(null);
  };

  if (!data || !data.tops.length) return null;

  const label = scopeLabel(node);

  return (
    <div className={`kd-gn-chip kd-gn-chip--${variant}${className ? ` ${className}` : ''}`}>
      <button
        ref={btnRef}
        type="button"
        className="kd-gn-chip__btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`지역: ${label} — 바꾸기`}
        onClick={toggle}
      >
        {/* ⑱ⓒ 어포던스 — 「전국 ▾」 만이면 로고 옆 정적 꼬리표로 읽혔다(region_chip_open 이틀 0, ⑰ 점검: 칩은 보이고 동작함).
         *    내비 칩(masthead·pagehead)에만 「지역」 표지. 홈 히어로 칩은 그 자리 문맥이 이미 지역 선택이라 그대로. 서버 HTML 고정 문자열(방문자 무관). */}
        {variant !== 'hero' ? <span className="kd-gn-chip__k" aria-hidden="true">지역</span> : null}
        <span className="kd-gn-chip__cur" data-kd-chip-label>{label}</span>
        <span className="kd-gn-chip__caret" aria-hidden="true">▾</span>
      </button>
      {geo && !open ? (
        <div className="kd-gn-suggest" role="group" aria-label="지역 제안" data-kd-geo-suggest={geo.code}>
          <span className="kd-gn-suggest__t">{geoSuggestText(geo)}</span>
          <span className="kd-gn-suggest__acts">
            <button type="button" className="kd-gn-suggest__btn kd-gn-suggest__btn--on" onClick={acceptGeo} data-kd-geo-act="accept">보기</button>
            <button type="button" className="kd-gn-suggest__btn" onClick={dismissGeo} data-kd-geo-act="dismiss">전국</button>
          </span>
        </div>
      ) : null}
      {open ? ((node: React.ReactNode) => (portal && typeof document !== 'undefined' ? createPortal(node, document.body) : node))(
        <>
          <div className="kd-gn-scrim" aria-hidden="true" onClick={close} />
          {/* VX L4-10 — 시트 안쪽은 KdRegionBar 와 한 몸(머리 = 바의 1행 「지역」+경로, 본문 = 좌 시·도 / 우 시·군).
           *  상태·적용·기억·계측은 위 apply() 그대로 — 새 상태 체계 없음. 패널 자리·크기(.kd-gn-panel)는 global-nav.css 그대로. */}
          <div ref={panelRef} id={panelId} className="kd-gn-panel kd-rs" role="dialog" aria-modal="true" aria-label="지역 선택">
            <KdRegionSheetView
              tops={tops}
              topCode={node?.parent?.code ?? node?.code ?? null}
              leaf={node?.parent ? { code: node.code, label: node.label } : null}
              expandedCode={expanded}
              q={q}
              hits={hits}
              onQuery={setQ}
              onExpand={setExpanded}
              onPick={(topCode, leafCode) => apply(topCode ? findScopeNode(data, leafCode ?? topCode) : null)}
              onClose={close}
            />
          </div>
        </>,
      ) : null}
    </div>
  );
}
